const chat = {
    combinedSummary: {
      heading: 'Overall summary',
      intro: 'A combined summary of the {{count}} selected files.',
      fileHeading: 'File {{index}}',
      insightsHeading: 'Technical findings and key points',
    },
    fileInsights: {
      failure: {
        heading: '🔍 Why failure analysis matters',
        bullet1: 'Identify the root cause and turn the countermeasures into a repeatable process',
        bullet2: 'Consider how to stop the same defect appearing on sibling products',
      },
      environment: {
        heading: '🌡️ Responding to environmental factors',
        bullet1: 'Assess the service environment properly and feed it back into the design',
        bullet2: 'Revisit the design criteria against the range of conditions seen in global markets',
      },
      design: {
        heading: '📐 Improving the design and development process',
        bullet1: 'Re-check the design criteria against real service conditions',
        bullet2: 'Strengthen design verification and add the missing evaluation items',
      },
      testing: {
        heading: '🧪 Strengthening test and evaluation',
        bullet1: 'Establish a test method that reproduces real service conditions',
        bullet2: 'Put the test equipment and evaluation process in place',
      },
      materials: {
        heading: '🔬 Optimising material and part selection',
        bullet1: 'Evaluate and select material properties suited to the service environment',
        bullet2: 'Verify long-term reliability and durability of the material',
      },
      general: {
        heading: '📋 General technical considerations',
        bullet1: 'Organise and share the documented findings systematically',
        bullet2: 'Broaden the findings by comparing them against similar cases',
      },
      common: {
        heading: '💡 Recommended actions',
        bullet1: 'Share and discuss the findings with the relevant engineers and departments',
        bullet2: 'Consider rolling the findings out to similar products and projects',
        bullet3: 'Fold the findings into ongoing continuous-improvement activity',
      },
    },
    sourceLabels: {
      webSource: 'Web source',
      untitledDocument: 'Untitled document',
    },
    placeholder: 'Ask, search, or make anything…',
    closePanel: 'Close panel',
    jumpToLatest: 'Jump to latest',
    stop: 'Stop',
    send: 'Send',
    emptyHeadline: 'Ask anything',
    emptyTagline: 'REVY answers from your internal documents, with citations.',
    addContext: 'Add context',
    attachFile: 'Attach file',
    attachDocument: 'Attach Document',
    allSources: 'REVY DB',
    allSourcesTooltip: 'Search internal documents only',
    assistant: 'Assistant',
    basedOn: 'Based on:',
    chatTab: 'Chat',
    commands: 'Commands',
    emptyTitle: 'Start the conversation by entering a message below',
    emptyDescription: 'I can help search documents, create summaries, and answer questions',
    enterKey: 'Enter',
    file: 'file',
    files: 'files',
    fileMentionHint: 'Type to search files or use @ to mention',
    fileSearchPlaceholder: 'Search files...',
    filesCount: '{{count}} files',
    filesSelected: '{{count}} selected',
    generateSummary: 'Generate Summary',
    inContext: 'In Context',
    webOnly: 'Web search (pending)',
    webOnlyTooltip: 'Web search is not run yet; answers use internal documents only',
    internalOnly: 'Internal docs only',
    internalOnlyTooltip: 'Search internal documents only',
    messagePlaceholder: 'Type a message...',
    modeSelectorTitle: 'Select mode',
    modeSelectorHint: 'You can also switch with /help, /ask, /plan, or /drbfm',
    newConversation: 'New Conversation',
    noFiles: 'No files',
    noFilesFound: 'No files found',
    openDocument: 'Open document',
    quickActions: 'Quick actions',
    referenceDocuments: 'Reference Documents',
    relevance: 'Relevance',
    relevanceHigh: 'High',
    relevanceMedium: 'Medium',
    relevanceLow: 'Low',
    relatedPassage: 'passage',
    relatedPassages: '{{count}} related passages',
    relatedPassagesUnit: 'passages',
    noPreview: 'No preview available',
    passageOnPage: 'Passage on page {{page}}',
    showRelatedPassages: 'Show {{count}} other related {{unit}} from this file',
    hideRelatedPassages: 'Hide other related passages from this file',
    searchDocuments: 'Search Documents',
    searchResultsCount: '{{count}} results',
    searchedFor: 'Searched for',
    settings: 'Chat settings',
    sourceLabel: 'Source: {{type}}',
    sourceLabelInternal: 'REVY DB',
    sourceLabelMixed: 'REVY DB + Web',
    sourceLabelWeb: 'Web',
    sourceListTitle: '{{title}} ({{count}})',
    sources: 'Sources',
    sourcesTab: 'Sources',
    systemMessage: 'System Message',
    thinking: 'Thinking...',
    stillThinking: 'Still thinking...',
    almostThere: 'Almost there...',
    sendMessage: 'Send message',
    inputPlaceholder: 'Ask, search, or make anything...',
    notionEmptyDescription: 'Ask REVY about documents or get help with writing, analysis, and related work.',
    newChat: 'New Chat',
    newChatConfirm: {
      title: 'Start a new chat',
      attachments: 'The files you have attached will not carry over. Are you sure you want to start a new chat?',
      streaming: 'The reply still being generated will be discarded. Are you sure you want to start a new chat?',
      confirm: 'Start new chat',
    },
    switchThreadConfirm: {
      title: 'Switch chat',
      attachments: 'The files you have attached will not carry over. Are you sure you want to switch?',
      streaming: 'The reply still being generated will be discarded. Are you sure you want to switch?',
      confirm: 'Switch',
    },
    retryWithModeConfirm: {
      title: 'Replace your draft',
      description:
        'The text you are typing will be replaced with the selected message. Are you sure you want to replace it?',
      confirm: 'Replace',
    },
    copyMessage: 'Copy message',
    copied: 'Copied',
    toSend: 'to send',
    webSearchDisabledNotice: 'Web search is unavailable or turned off, so this answer draws only on internal documents.',
    newThread: 'New Thread',
    chatFallback: 'Chat',
    modeSwitched: 'Switched to "{{mode}}" mode. {{description}}',
    noFilesSelectedForSummary: 'No files are selected. Select a file and try again.',
    deepAnalysisInProgress: 'Running a detailed technical analysis...',
    fileAnalysisInProgress: 'Analyzing files...',
    summaryUnavailable: 'The summary could not be retrieved.',
    summary: 'summary',
    deepTechnicalAnalysis: 'detailed technical analysis',
    singleFileSummaryHeading: '## {{title}} {{type}}',
    multiFileSummaryHeading: '## {{type}} for selected files ({{count}} files)',
    individualFileSummariesHeading: '### Individual file summaries',
    individualFileAnalysesHeading: '### Individual file technical analyses',
    combinedTechnicalAnalysisHeading: '### Combined technical analysis',
    combinedTechnicalInsightsHeading: '### Combined technical insights',
    summaryError: 'An error occurred while summarizing files. Please try again.',
    streamError: 'Generating the answer failed. Please try again.',
    storageUnavailable: 'Your message could not be saved because chat storage is '
      + 'unreachable. Please try again shortly.',
    askingAboutFiles: '📎 Asking about {{count}} files: {{fileNames}}',
    fileAddedToContext: '✅ Added file to context: {{name}}',
    history: 'Chat history',
    noHistory: 'No chat history',
    pinned: 'Pinned',
    recentChats: 'Recent chats',
    pinThread: 'Pin',
    unpinThread: 'Unpin',
    pinThreadAria: 'Pin {{title}}',
    unpinThreadAria: 'Unpin {{title}}',
    verificationBasedOnDocuments: 'Based on documents',
    verificationPartlyUnsupported: 'Partly unsupported',
    verificationNeedsReviewLabel: 'Needs review',
    verificationDetailsTitle: 'Answer grounding check',
    verificationDetailsDescription: 'Highlighted parts in the answer show verification results. Green means the statement is supported by documents, and orange means support could not be confirmed.',
    verificationSupportedClaims: 'Supported by documents (green): {{count}} sentences',
    verificationUnsupportedClaims: 'Unsupported (orange): {{count}} sentences',
    verificationWeakCitations: 'Low-relevance references: {{count}} items',
    verificationSupportedTitle: 'Supported by documents',
    verificationUnsupportedTitle: 'Unsupported',
    generalFallbackWarning: 'This answer is not based on knowledge-base documents. Please verify its accuracy because it uses general knowledge.',
    completenessMissingNotice: 'Completeness check: classification values that could not be mapped to answer items (verbatim from the source):',
    completenessNotEvaluatedNotice: 'Not evaluated: classification values outside the supported value shape, excluded from the completeness check (display labels: long or non-printable values are shortened or masked):',
    verificationNeedsReview: 'Some grounding needs review. Open the answer grounding check above for details.',
    retryWithMode: 'Retry in another mode →',
    deleteConfirm: 'Are you sure you want to delete this document? This action cannot be undone.',
    editTags: 'Edit tags',
    tagInputPlaceholder: 'Type a tag and press Enter',
    viewSource: 'View source',
    modes: {
      help: {
        name: 'Help',
        description: 'System usage and command list',
      },
      settings: {
        name: 'Settings',
        description: 'Data source status and usage statistics',
      },
      ask: {
        name: 'Q&A',
        description: 'Question answering mode (default)',
      },
      plan: {
        name: 'Design Deep Dive',
        description: 'Deep discussion about design',
      },
      drbfm: {
        name: 'DRBFM Analysis',
        description: 'DRBFM analysis support',
      },
      verification: {
        name: 'Design Verification',
        description: 'Analyze and improve design verification items',
      },
      cad: {
        name: 'CAD Analysis',
        description: 'CAD data structure and design review',
      },
    },
  };

export default chat;
